import Promise from 'bluebird';
import _ from 'underscore';
import { useContext, useMemo, useCallback } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useWizardService } from '@bingads-webui-react/wizard';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { loadBundleCountries, getErrorsPlaceholders } from '@bingads-webui-campaign-react/xandr-campaign';
import { withDFMCache } from '@bingads-webui/dfm-cache';
import { mergeData } from '@bingads-webui-campaign-react/product-services';
import { getFeedData } from '@bingads-webui-campaign/audience-campaign-creation-wizard';

import {
  loadCampaign,
  loadAdGroup,
  readDayTimeCriterions,
  readLocationCriterions,
  readAdGroupLocationCriterions,
  readAgeAndGenderTargetingCriterions,
  readAdGroupAgeAndGenderCriterions,
  modelToViewModelForAdGroup,
  modelToViewModelForCampaign,
  viewModelToModelForCampaign,
  viewModelToModelForConversionGoals,
  saveAdGroup,
  saveCampaign,
} from '../../model';
import { isSearchShoppingCampaign, allSettledPromiseWrapper } from '../step-utils';
import { useWizardLog, LOG_STATUS, getProductData } from '../../shared';

const getFeedDataWithCache = withDFMCache(getFeedData, 'getFeedScopeDFM', {});

export const useSummaryRepository = () => {
  const i18n = useContext(I18nContext);
  const { state } = useWizardService();

  const { scope } = useContext(ScopeContext) || {};
  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const isPAAIMConsolidationEnabled = _.get(serverContext, ['DynamicPermissions', 'PAAIMConsolidation'], false);
  const isOdataGetProductScopeEnabled = _.get(serverContext, ['DynamicPermissions', 'OdataGetProductScope'], false);

  const scenarioContext = useContext(ScenarioContext);
  const activity = useContext(ActivityContext);

  const internalGetProductData = useCallback(params => getProductData(params, serverContext), [serverContext]);
  const getAudienceProductDataWithCache = withDFMCache(internalGetProductData, 'getAudienceProductScopeDFM', {});
  const getProductDataWithCache = withDFMCache(internalGetProductData, 'getProductScopeDFM', {});

  const { cid, aid } = scope.values;

  const { flow, isPublishing } = state.context.wizardData;
  const isSearch = isSearchShoppingCampaign(flow);
  const { onStepLoadedLog, onStepSavedLog } = useWizardLog({ activity, wizardState: state, isSearch });


  const load = () => {
    onStepLoadedLog({ status: LOG_STATUS.LOAD_START });
    const savedCampaign = state.context.wizardData.campaign;
    const savedAdGroup = state.context.wizardData.adGroup;
    const savedProductData = state.context.wizardData.productData;
    const savedFeedData = state.context.wizardData.feedData;
    // const adGroupId = savedAdGroup ? savedAdGroup.Id : 1180875542953561;
    // const campaignId = savedCampaign ? savedCampaign.Id : 946842629; feedlabel
    const adGroupId = savedAdGroup ? savedAdGroup.Id : null;
    const campaignId = savedCampaign ? savedCampaign.Id : null;
    // const adGroupId = savedAdGroup ? savedAdGroup.Id : 1194069858307329;//null;
    // const campaignId = savedCampaign ? savedCampaign.Id : 1046593706;//null; dynamicFeed

    const isSearchShopping = isSearch;

    const loadPromises = {
      adGroup: savedAdGroup ? Promise.resolve(savedAdGroup) : loadAdGroup(cid, aid, campaignId, adGroupId),
      campaign: savedCampaign ? Promise.resolve({
        ...viewModelToModelForCampaign({
          data: savedCampaign,
        }),
        ...viewModelToModelForConversionGoals({
          data: savedCampaign,
        }),
      }) : loadCampaign(cid, aid, campaignId),
      locationCriterions: isSearchShopping ? readLocationCriterions({ cid, aid, campaignId }) : readAdGroupLocationCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      dayTimeCriterions: readDayTimeCriterions({ cid, aid, campaignId }),
      targetCriterions: isSearchShopping ? readAgeAndGenderTargetingCriterions({ cid, aid, campaignId }) : readAdGroupAgeAndGenderCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      bundleCountriesAndLocationCriterions: loadBundleCountries({ cid, aid, serverContext }),
    };

    if (!isSearchShopping) {
      // When refresh in local, the data-fetch-manager will get product data without campaign Id and set cache. We can't get the campaignCriterion.
      // TODO: include permissions for CuriseFeedForAudienceCampaign 1073
      loadPromises.feedData = savedFeedData ? Promise.resolve(savedFeedData) : getFeedDataWithCache({ $top: 10000 /* permissions */ });
    } else {
      loadPromises.feedData = Promise.resolve(null);
    }

    if (!savedProductData) {
      const params = { campaignId };

      if (!isSearchShopping) {
        params.getCategoryFullData = true;
        params.getProductTypeFullData = true;
        params.salesCountry = 'US';
      }

      if (isOdataGetProductScopeEnabled) {
        const p = isSearchShopping ? getProductDataWithCache(params) : getAudienceProductDataWithCache(params);
        loadPromises.productData = p.then(pData => mergeData(pData));
      } else {
        loadPromises.productData = isSearchShopping ? getProductDataWithCache(params) : getAudienceProductDataWithCache(params);
      }
    } else {
      loadPromises.productData = Promise.resolve(savedProductData);
    }

    return Promise.props(loadPromises).then(({
      adGroup,
      campaign,
      locationCriterions,
      dayTimeCriterions,
      targetCriterions,
      bundleCountriesAndLocationCriterions,
      productData,
      feedData,
    }) => {
      onStepLoadedLog({ status: LOG_STATUS.LOAD_SUCCESS });
      const adGroupData = modelToViewModelForAdGroup({
        adGroupModel: adGroup,
        campaignModel: campaign,
        i18n,
        isAudienceShoppingCampaign: !isSearchShopping,
        locationCriterionsModel: !isSearchShopping && locationCriterions ? locationCriterions.value : [],
        targetCriterionsModel: !isSearchShopping && targetCriterions ? targetCriterions.value : [],
      });

      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        locationCriterionsModel: isSearchShopping && locationCriterions ? locationCriterions.value : [],
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        targetCriterionsModel: isSearchShopping && targetCriterions ? targetCriterions.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        productData,
        feedData,
        permissions,
      });

      return {
        adGroup: adGroupData.data,
        campaign: campaignData.data,
      };
    });
  };

  const save = () => {
    onStepSavedLog({ status: LOG_STATUS.SAVE_START });
    const savedCampaign = state.context.wizardData.campaign;
    const savedAdGroup = state.context.wizardData.adGroup;

    if (isPublishing) {
      savedCampaign.Status = 'Active';
      savedAdGroup.Status = 'Active';
    } else {
      savedCampaign.Status = 'Paused';
      savedAdGroup.Status = 'Paused';
    }

    const savePromises = [
      saveAdGroup({
        adGroup: { data: savedAdGroup },
        campaign: savedCampaign,
        isProductGroupAllowedForAudienceCampaign: isPAAIMConsolidationEnabled,
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveDefaultAdGroup'),
        i18n,
      }),
      saveCampaign({
        campaign: { data: savedCampaign },
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
        i18n,
      }),
    ];

    return new Promise((resolve, reject) => {
      allSettledPromiseWrapper(savePromises).then((results) => {
        const allSuccess = _.every(results, result => result.status === 'fulfilled');

        if (allSuccess) {
          const campaignData = results[1].value.campaign.data;
          onStepSavedLog({ status: LOG_STATUS.SAVE_SUCCESS, msg: `Status: ${campaignData.Status}, Id: ${campaignData.Id}` });
          return resolve({
            adGroup: results[0].value.adGroup.data,
            campaign: results[1].value.campaign.data,
            ...getErrorsPlaceholders(),
          });
        }

        const errors = _.map(results, (result) => {
          if (result.status === 'fulfilled') {
            return null;
          }

          const { serverErrors = {} } = result.reason;

          return serverErrors;
        });
        onStepSavedLog({ status: LOG_STATUS.SAVE_FAIL, msg: errors });
        // eslint-disable-next-line prefer-promise-reject-errors
        return reject({ error: _.flatten(errors.filter(val => val !== null)) });
      });
    });
  };

  return {
    load,
    save,
  };
};
